import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { EmojiPicker } from "@/components/EmojiPicker";
import { TemplatePicker } from "@/components/TemplatePicker";
import { MessageAttachments, AttachmentButton } from "@/components/InboxAttachments";
import { toDate, fmtTime, fmtDateShort, fmtDateLong } from "@/lib/datetime";
import { useNavigate } from "react-router-dom";
import { useProperty } from "@/contexts/PropertyContext";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import {
  PaperPlaneTilt,
  ChatCircleDots,
  ArrowsClockwise,
  MagnifyingGlass,
  CaretLeft,
  X as XIcon,
  CircleNotch,
  Lock,
  CheckCircle,
  Buildings,
  Bed,
  Calendar,
  Question,
  Hourglass,
  Clock,
  CalendarX,
  CreditCard,
  Warning,
  ThumbsUp,
  ThumbsDown,
  Check,
  Translate,
  Funnel,
  Tag,
  Coins,
  Prohibit,
  CalendarCheck,
} from "@phosphor-icons/react";
import { FilterSelect } from "@/components/ListPage";

// =============================================================================
// Channel meta
// =============================================================================
const PROVIDERS = {
  BookingCom: { label: "Booking.com", color: "#003580", short: "BK" },
  AirBNB:     { label: "Airbnb",      color: "#FF5A5F", short: "AB" },
  Expedia:    { label: "Expedia",     color: "#FFC500", short: "EX" },
};
const PROVIDER_LABELS = { booking_com: "Booking.com", airbnb: "Airbnb", expedia: "Expedia", direct: "Direktno" };
const PROVIDER_KEYS = Object.keys(PROVIDERS);

function ProviderBadge({ provider, size = "sm" }) {
  const p = PROVIDERS[provider] || { label: provider || "?", color: "#64748b", short: "?" };
  const px = size === "sm" ? "h-6 w-6 text-[10px]" : "h-8 w-8 text-xs";
  return (
    <div className={`flex shrink-0 items-center justify-center rounded font-bold text-white ${px}`} style={{ backgroundColor: p.color }} title={p.label}>
      {p.short}
    </div>
  );
}

function relTime(iso) {
  if (!iso) return "";
  const d = toDate(iso); const now = new Date();
  const s = Math.floor((now - d) / 1000);
  if (s < 60) return "sad";
  if (s < 3600) return `${Math.floor(s / 60)}min`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 604800) return `${Math.floor(s / 86400)}d`;
  return fmtDateShort(d);
}

function fmtStay(a, b) {
  const f = (iso) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.` : "");
  return `${f(a)} – ${f(b)}${b ? b.slice(0, 4) + "." : ""}`;
}

function formatDayHeader(iso) {
  if (!iso) return "";
  return fmtDateLong(iso);
}

// =============================================================================
// Interactive message detection (Booking.com system messages)
// =============================================================================
// Booking.com šalje sistemske poruke s meta polja koja signaliziraju tip zahtjeva.
// Detekcija je defenzivna — pokušavamo više poznatih formata jer Channex ponekad
// prosljeđuje raw OTA payload, a ponekad normaliziran. Vraća `null` za obične
// poruke, inače `{ type, lang, requested_time?, requested_date?, raw_meta }`.
function detectInteractive(msg) {
  if (!msg || msg.sender !== "guest") return null;
  const meta = msg.meta || {};
  const text = (msg.text || "").toLowerCase();

  // Already acted on — hide buttons
  if (meta.action_taken) {
    return { type: "already_acted", action: meta.action_taken.action, at: meta.action_taken.at, by: meta.action_taken.by_user_email };
  }

  // 1) Explicit Booking.com message types (if Channex forwards them)
  const bType = (meta.booking_com_message_type || meta.message_type || "").toLowerCase();
  const bTopic = (meta.self_service_topic || meta.topic || "").toLowerCase();

  if (bType === "self_service_event" && bTopic === "checkin") {
    return { type: "early_checkin", requested_time: meta.requested_time, requested_date: meta.requested_date, raw_meta: meta };
  }
  if (bType === "self_service_event" && bTopic === "checkout") {
    return { type: "late_checkout", requested_time: meta.requested_time, requested_date: meta.requested_date, raw_meta: meta };
  }
  if (bType === "request_to_book_event" || meta.request_to_book) {
    return { type: "request_to_book", raw_meta: meta };
  }
  if (bType === "cancellation_request" || meta.cancellation_requested) {
    return { type: "cancellation", raw_meta: meta };
  }
  if (bType === "cc_details" || meta.credit_card_details || meta.cc_received) {
    return { type: "cc_details", raw_meta: meta };
  }
  if (bType === "special_request" || meta.special_request) {
    return { type: "special_request", raw_meta: meta };
  }

  // 2) Heuristic fallback (text-based) — Booking.com's system messages are
  //    predictable English phrases. Ovo pomaže ako Channex ne prosljeđuje meta.
  if (/\bearly check[- ]?in\b|\barrive earl/.test(text) && /\brequest|would like|possible\b/.test(text)) {
    return { type: "early_checkin", raw_meta: meta, heuristic: true };
  }
  if (/\blate check[- ]?out\b|\blater departure\b/.test(text) && /\brequest|would like|possible\b/.test(text)) {
    return { type: "late_checkout", raw_meta: meta, heuristic: true };
  }
  if (/\bcancel(lation)?\b/.test(text) && /\brequest|would like\b/.test(text)) {
    return { type: "cancellation", raw_meta: meta, heuristic: true };
  }
  if (/\bcredit card\b.*(detail|number|cvv)|\bpayment detail/.test(text)) {
    return { type: "cc_details", raw_meta: meta, heuristic: true };
  }
  if (/\bspecial request\b/.test(text)) {
    return { type: "special_request", raw_meta: meta, heuristic: true };
  }

  return null;
}

const INTERACTIVE_META = {
  early_checkin: {
    icon: Clock,
    color: "sky",
    title: "Zahtjev za raniji check-in",
    desc: (d) => d.requested_time ? `Gost traži check-in u ${d.requested_time}.` : "Gost traži raniji check-in nego što je standardno.",
    actions: [
      { key: "approve_early_checkin", label: "Odobri", tone: "success", icon: ThumbsUp },
      { key: "decline_early_checkin", label: "Odbij",  tone: "danger",  icon: ThumbsDown },
    ],
  },
  late_checkout: {
    icon: Clock,
    color: "indigo",
    title: "Zahtjev za kasniji check-out",
    desc: (d) => d.requested_time ? `Gost traži check-out u ${d.requested_time}.` : "Gost traži kasniji check-out nego što je standardno.",
    actions: [
      { key: "approve_late_checkout", label: "Odobri", tone: "success", icon: ThumbsUp },
      { key: "decline_late_checkout", label: "Odbij",  tone: "danger",  icon: ThumbsDown },
    ],
  },
  special_request: {
    icon: Warning,
    color: "amber",
    title: "Posebni zahtjev gosta",
    desc: () => "Gost je poslao posebni zahtjev vezan uz boravak. Pregledajte poruku i odgovorite.",
    actions: [
      { key: "acknowledge_special_request", label: "Prihvaćam", tone: "success", icon: Check },
      { key: "decline_special_request",     label: "Ne mogu",   tone: "danger",  icon: XIcon },
    ],
  },
  cc_details: {
    icon: CreditCard,
    color: "emerald",
    title: "Podaci o kreditnoj kartici",
    desc: () => "Booking.com je proslijedio CC podatke gosta. Potvrdite primitak nakon obrade.",
    actions: [
      { key: "acknowledge_cc_details", label: "Potvrdi primitak", tone: "success", icon: Check },
    ],
  },
  cancellation: {
    icon: CalendarX,
    color: "rose",
    title: "Zahtjev za otkazivanje",
    desc: () => "Gost je poslao zahtjev za otkazivanje rezervacije.",
    actions: [
      { key: "confirm_cancellation", label: "Potvrdi otkazivanje", tone: "neutral", icon: Check },
    ],
  },
  request_to_book: {
    icon: Hourglass,
    color: "amber",
    title: "Request to Book",
    desc: () => "Pre-rezervacijski zahtjev. Odgovorite kroz standardni Booking Extranet workflow.",
    actions: [],
  },
};

const TONE_CLASSES = {
  success: "bg-emerald-600 hover:bg-emerald-700 text-white",
  danger:  "bg-rose-600 hover:bg-rose-700 text-white",
  neutral: "bg-slate-600 hover:bg-slate-700 text-white",
};

const COLOR_CLASSES = {
  sky:     { border: "border-sky-300",     bg: "bg-sky-50",     fg: "text-sky-900",     iconBg: "bg-sky-100",     iconFg: "text-sky-700" },
  indigo:  { border: "border-indigo-300",  bg: "bg-indigo-50",  fg: "text-indigo-900",  iconBg: "bg-indigo-100",  iconFg: "text-indigo-700" },
  amber:   { border: "border-amber-300",   bg: "bg-amber-50",   fg: "text-amber-900",   iconBg: "bg-amber-100",   iconFg: "text-amber-700" },
  emerald: { border: "border-emerald-300", bg: "bg-emerald-50", fg: "text-emerald-900", iconBg: "bg-emerald-100", iconFg: "text-emerald-700" },
  rose:    { border: "border-rose-300",    bg: "bg-rose-50",    fg: "text-rose-900",    iconBg: "bg-rose-100",    iconFg: "text-rose-700" },
};

// =============================================================================
// Main page
// =============================================================================
export default function InboxPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [filter, setFilter] = useState("all"); // all | BookingCom | AirBNB | Expedia
  const [status, setStatus] = useState("open"); // open | closed | all
  const [search, setSearch] = useState("");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [unansweredOnly, setUnansweredOnly] = useState(false);
  const [inquiriesOnly, setInquiriesOnly] = useState(false);
  const [stay, setStay] = useState(""); // "" | upcoming | inhouse | departed
  const [showFilters, setShowFilters] = useState(() => { try { return localStorage.getItem("rw_inbox_filters_open") === "1"; } catch { return false; } });
  useEffect(() => { try { localStorage.setItem("rw_inbox_filters_open", showFilters ? "1" : "0"); } catch { /* ignore */ } }, [showFilters]);
  const { activePropertyId } = useProperty();
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [reservation, setReservation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [composer, setComposer] = useState("");
  const [actionMsg, setActionMsg] = useState("");
  const [showThreadMobile, setShowThreadMobile] = useState(false);
  const msgEndRef = useRef(null);
  const scrollMsgsToEnd = () => {
    const box = msgEndRef.current?.parentElement;
    if (box) box.scrollTo({ top: box.scrollHeight, behavior: "smooth" });
  };

  const loadSeqRef = useRef(0);
  const load = useCallback(async () => {
    const seq = ++loadSeqRef.current;
    const params = {};
    if (filter !== "all") params.provider = filter;
    if (status !== "all") params.status = status;
    if (search) params.q = search;
    if (unansweredOnly) params.unanswered_only = true;
    if (inquiriesOnly) params.inquiries_only = true;
    if (stay) params.stay = stay;
    if (activePropertyId) params.property_id = activePropertyId;
    const { data } = await api.get("/inbox/threads", { params });
    // Guard against out-of-order responses when filters are toggled rapidly —
    // only the most recently fired request is allowed to update state.
    if (seq !== loadSeqRef.current) return;
    setItems(data.items || []);
    setLoading(false);
  }, [filter, status, search, unansweredOnly, inquiriesOnly, stay, activePropertyId]);

  // initial + filter change
  useEffect(() => { load(); }, [load]);

  // Deep link from Calendar: /inbox?thread=<id>
  useEffect(() => {
    const tid = new URLSearchParams(window.location.search).get("thread");
    if (tid) { setStatus("all"); setSelectedId(tid); setShowThreadMobile(true); }
  }, []);

  // 30s polling of the thread list
  useEffect(() => {
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  // open thread
  useEffect(() => {
    if (!selectedId) { setSelected(null); setMessages([]); setReservation(null); return; }
    api.get(`/inbox/threads/${selectedId}`).then(({ data }) => {
      setSelected(data.thread);
      setMessages(data.messages || []);
      setReservation(data.reservation || null);
      // refresh the list to clear unread dot
      load();
      setTimeout(() => scrollMsgsToEnd(), 50);
    });
  }, [selectedId, load]);

  // poll messages of open thread
  useEffect(() => {
    if (!selectedId) return;
    const id = setInterval(() => {
      api.post(`/inbox/threads/${selectedId}/refresh`, {}).then(() => {
        api.get(`/inbox/threads/${selectedId}`).then(({ data }) => {
          setMessages(data.messages || []);
          setSelected(data.thread);
        });
      }).catch(() => {});
    }, 30000);
    return () => clearInterval(id);
  }, [selectedId]);

  const shown = useMemo(() => (unreadOnly ? items.filter((t) => t.unread || t.id === selectedId) : items), [items, unreadOnly, selectedId]);
  const unreadCount = useMemo(() => items.filter((t) => t.unread).length, [items]);
  const inboxFilterCount = useMemo(() => [filter !== "all", stay !== "", unreadOnly, unansweredOnly, inquiriesOnly].filter(Boolean).length, [filter, stay, unreadOnly, unansweredOnly, inquiriesOnly]);
  // "Needs a decision" bucket = unresolved Live Feed inquiry/reservation/
  // alteration requests only — once resolved (any type, including inquiry),
  // the thread drops back into the normal "Razgovori" list.
  const needsAction = (t) => (t.live_feed_type ? !t.live_feed_resolved : t.is_inquiry);
  const inquiries = useMemo(() => shown.filter(needsAction), [shown]);
  const regular = useMemo(() => shown.filter((t) => !needsAction(t)), [shown]);

  const [messagesApiUnavailable, setMessagesApiUnavailable] = useState(false);
  const [noPropertyMapped, setNoPropertyMapped] = useState(false);

  const onSync = async () => {
    setSyncing(true);
    setActionMsg("");
    try {
      const { data } = await api.post("/inbox/sync", {});
      api.post("/inbox/live-feed/pull", {}).catch(() => {}); // fire-and-forget: refresh Airbnb inquiry/request cards too
      const errs = data.errors || [];
      const hasScopeErr = errs.some((e) => (e || "").toLowerCase().includes("messaging još nije aktivan"));
      setMessagesApiUnavailable(hasScopeErr);
      setNoPropertyMapped(false);
      if (hasScopeErr) {
        setActionMsg(errs[0]);
      } else {
        setActionMsg(`Sinkronizirano ${data.synced} razgovora${errs.length ? ` (${errs.length} greška)` : ""}`);
      }
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (e?.response?.status === 400 && detail.toLowerCase().includes("nema povezanog smještaja")) {
        setNoPropertyMapped(true);
        setActionMsg(detail);
      } else {
        setActionMsg(formatApiError(e));
      }
    } finally {
      setSyncing(false);
    }
  };

  const onSend = async (override) => {
    const text = (typeof override === "string" ? override : composer).trim();
    if (!text || !selectedId) return;
    setSending(true);
    try {
      await api.post(`/inbox/threads/${selectedId}/reply`, { message: text });
      setComposer("");
      const { data } = await api.get(`/inbox/threads/${selectedId}`);
      setMessages(data.messages || []);
      setSelected(data.thread);
      setTimeout(() => scrollMsgsToEnd(), 50);
      await load();
      return true;
    } catch (e) {
      setActionMsg(formatApiError(e));
      return false;
    } finally {
      setSending(false);
    }
  };

  const onClose = async () => {
    if (!selected || !window.confirm("Zatvoriti ovaj razgovor?")) return;
    try {
      await api.post(`/inbox/threads/${selected.id}/close`, {});
      const { data } = await api.get(`/inbox/threads/${selected.id}`);
      setSelected(data.thread);
      await load();
    } catch (e) { setActionMsg(formatApiError(e)); }
  };

  const onLiveFeedDone = async () => {
    if (!selected) return;
    const { data } = await api.get(`/inbox/threads/${selected.id}`);
    setSelected(data.thread);
    await load();
  };

  const onNoReplyNeeded = async () => {
    if (!selected) return;
    try {
      await api.post(`/inbox/threads/${selected.id}/no-reply-needed`, {});
      setActionMsg("Označeno kao 'No reply needed' za Booking.com.");
      await load();
    } catch (e) { setActionMsg(formatApiError(e)); }
  };

  const onSelectThread = (id) => {
    setSelectedId(id);
    setShowThreadMobile(true);
  };

  return (
    <div className="flex h-[calc(100dvh-64px-64px)] overflow-hidden lg:h-[calc(100dvh-64px)]" data-testid="inbox-page">
      {/* ============================= LEFT: list ============================= */}
      <div className={`${showThreadMobile ? "hidden" : "flex"} min-h-0 w-full flex-col border-r border-border bg-background lg:flex lg:w-[400px] xl:w-[440px]`}>
        {/* Header */}
        <div className="border-b border-border bg-card px-4 py-3">
          <div className="flex items-center justify-between">
            <h1 className="flex items-center gap-2 text-lg font-semibold text-foreground">
              <ChatCircleDots size={22} weight="duotone" /> Inbox
            </h1>
            <button onClick={onSync} disabled={syncing} className="flex items-center gap-1 rounded-md border border-border bg-background px-2.5 py-1.5 text-xs font-medium hover:bg-muted disabled:opacity-50" data-testid="inbox-sync-btn">
              {syncing ? <CircleNotch size={14} className="animate-spin" /> : <ArrowsClockwise size={14} />} Osvježi
            </button>
          </div>

          {messagesApiUnavailable && (
            <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-[11px] leading-tight text-amber-900" data-testid="inbox-scope-warning">
              <p className="font-semibold">Messaging još nije aktivan</p>
              <p className="mt-1">Da omogućiš primanje OTA poruka: kontaktiraj podršku da uključi "Messages" feature i na Booking.com/Airbnb/Expedia daj messaging permission tijekom OAuth setupa.</p>
            </div>
          )}
          {noPropertyMapped && (
            <div className="mt-3 rounded-md border border-rose-300 bg-rose-50 p-3 text-[11px] leading-tight text-rose-900" data-testid="inbox-no-property-warning">
              <p className="font-semibold">Nema povezanog smještaja</p>
              <p className="mt-1">Nijedan smještaj nije povezan s channel managerom. Otvori <strong>Postavke → Kanali</strong> i poveži barem jedan smještaj prije prve sinkronizacije Inboxa.</p>
            </div>
          )}

          {/* Search */}
          <div className="relative mt-3">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Pretraži razgovore..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-md border border-foreground/20 bg-card py-2 pl-8 pr-3 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              data-testid="inbox-search"
            />
          </div>

          {/* Status (always visible) + collapsible filter panel */}
          <div className="mt-3 flex items-center gap-2">
            <div className="flex flex-1 items-center gap-0.5 rounded-md border border-border bg-muted/40 p-0.5">
              {[["open", "Otvoreni"], ["closed", "Zatvoreni"], ["all", "Svi"]].map(([k, l]) => (
                <button
                  key={k}
                  onClick={() => setStatus(k)}
                  className={`flex-1 rounded px-2 py-1.5 text-xs font-medium transition ${status === k ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:bg-muted"}`}
                  data-testid={k === "all" ? "chip-all-status" : `chip-${k}`}
                >
                  {l}
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowFilters((v) => !v)}
              className={`relative inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium transition-colors ${showFilters ? "border-primary bg-primary/10 text-primary" : "border-border bg-card text-foreground hover:bg-muted"}`}
              aria-expanded={showFilters}
              data-testid="inbox-filter-toggle"
            >
              <Funnel size={14} weight={showFilters ? "fill" : "regular"} /> Filtri
              {inboxFilterCount > 0 && (
                <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[9px] font-bold text-primary-foreground">{inboxFilterCount}</span>
              )}
            </button>
          </div>

          {showFilters && (
            <div className="mt-2 space-y-2 rounded-md border border-border bg-muted/20 p-2.5" data-testid="inbox-filter-panel">
              <FilterSelect value={filter} onChange={setFilter} testId="inbox-filter-channel" options={[
                { value: "all", label: "Svi kanali" }, ...PROVIDER_KEYS.map((p) => ({ value: p, label: PROVIDERS[p].label })),
              ]} />
              <FilterSelect value={stay} onChange={setStay} testId="inbox-filter-stay" options={[
                { value: "", label: "Svi boravci" }, { value: "upcoming", label: "Dolaze" }, { value: "inhouse", label: "U smještaju" }, { value: "departed", label: "Otišli" },
              ]} />
              <div className="flex flex-wrap gap-1.5 pt-0.5">
                <FilterChip active={unreadOnly} onClick={() => setUnreadOnly((v) => !v)} testId="chip-unread">
                  Nepročitane{unreadCount > 0 ? ` (${unreadCount})` : ""}
                </FilterChip>
                <FilterChip active={unansweredOnly} onClick={() => setUnansweredOnly((v) => !v)} testId="chip-unanswered">Bez odgovora</FilterChip>
                <FilterChip active={inquiriesOnly} onClick={() => setInquiriesOnly((v) => !v)} testId="chip-inquiries">Upiti bez rezervacije</FilterChip>
              </div>
            </div>
          )}
        </div>

        {/* Thread list */}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-card" data-testid="inbox-thread-list">
          {loading ? (
            <div className="flex h-32 items-center justify-center"><CircleNotch size={20} className="animate-spin text-muted-foreground" /></div>
          ) : items.length === 0 ? (
            <EmptyState onSync={onSync} syncing={syncing} />
          ) : shown.length === 0 ? (
            <div className="px-6 py-12 text-center text-sm text-muted-foreground" data-testid="inbox-no-unread">Nema nepročitanih razgovora</div>
          ) : (
            <>
              {inquiries.length > 0 && (
                <>
                  <SectionHeader icon={Question} title={`Čeka odgovor (${inquiries.length})`} tone="amber" />
                  {inquiries.map((t) => (
                    <ThreadRow key={t.id} thread={t} selected={selectedId === t.id} onClick={() => onSelectThread(t.id)} />
                  ))}
                </>
              )}
              {regular.length > 0 && (
                <>
                  {inquiries.length > 0 && <SectionHeader icon={ChatCircleDots} title="Razgovori" tone="slate" />}
                  {regular.map((t) => (
                    <ThreadRow key={t.id} thread={t} selected={selectedId === t.id} onClick={() => onSelectThread(t.id)} />
                  ))}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* ============================= RIGHT: conversation ============================= */}
      <div className={`${showThreadMobile ? "flex" : "hidden"} min-h-0 min-w-0 flex-1 flex-col bg-background lg:flex`}>
        {!selected ? (
          <div className="flex flex-1 flex-col items-center justify-center text-muted-foreground bg-card">
            <ChatCircleDots size={48} weight="duotone" className="mb-3 opacity-40" />
            <p>Odaberite razgovor iz liste</p>
          </div>
        ) : (
          <>
            {/* Conversation header */}
            <div className="flex items-center gap-3 border-b border-border bg-card px-4 py-3">
              <button onClick={() => setShowThreadMobile(false)} className="rounded p-1 text-muted-foreground hover:bg-muted lg:hidden" data-testid="inbox-back-btn">
                <CaretLeft size={20} />
              </button>
              <ProviderBadge provider={selected.provider} size="md" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="truncate text-base font-semibold text-foreground">{selected.title || "(Bez imena)"}</p>
                  {needsAction(selected) && (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                      {selected.live_feed_type === "alteration_request" ? "IZMJENA" : selected.live_feed_type === "reservation_request" ? "ZAHTJEV" : "UPIT"}
                    </span>
                  )}
                  {selected.is_closed && (
                    <span className="flex items-center gap-1 rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-semibold text-slate-700">
                      <Lock size={10} /> ZATVORENO
                    </span>
                  )}
                </div>
                <p className="truncate text-xs text-muted-foreground" data-testid="inbox-header-stay">
                  {PROVIDERS[selected.provider]?.label || PROVIDER_LABELS[selected.provider] || selected.provider}
                  {reservation && <> · {fmtStay(reservation.check_in, reservation.check_out)}{reservation.unit_code ? ` · ${reservation.unit_code}` : ""}</>}
                </p>
              </div>
              <div className="flex items-center gap-1">
                {reservation && (
                  <button onClick={() => navigate(`/calendar?reservation=${reservation.id}`)} className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1.5 text-xs font-medium hover:bg-muted" data-testid="inbox-open-calendar" title="Otvori rezervaciju u kalendaru">
                    <Calendar size={13} /> <span className="hidden sm:inline">Kalendar</span>
                  </button>
                )}
                {selected.provider === "BookingCom" && !selected.is_closed && (
                  <button onClick={onNoReplyNeeded} className="hidden rounded-md border border-border bg-background px-2 py-1.5 text-xs font-medium hover:bg-muted sm:inline-flex" data-testid="inbox-no-reply" title="Booking.com: označi da nije potreban odgovor">
                    No reply
                  </button>
                )}
                {!selected.is_closed && (
                  <button onClick={onClose} className="rounded-md border border-border bg-background px-2 py-1.5 text-xs font-medium hover:bg-muted" data-testid="inbox-close-thread">
                    Zatvori
                  </button>
                )}
              </div>
            </div>

            {/* Airbnb Live Feed — inquiry / reservation_request / alteration_request */}
            {selected.live_feed_type ? (
              <div className="border-b border-border px-3 py-2 sm:px-4">
                <LiveFeedActionCard key={selected.id} thread={selected} onDone={onLiveFeedDone} />
              </div>
            ) : selected.is_inquiry && selected.inquiry_meta && (
              <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900">
                <p className="font-semibold">Detalji upita:</p>
                <p>{selected.inquiry_meta.checkin_date} → {selected.inquiry_meta.checkout_date} · {selected.inquiry_meta.number_of_guests} {selected.inquiry_meta.number_of_guests === 1 ? "gost" : "gostiju"} · {selected.inquiry_meta.expected_payout_amount_accurate} {selected.inquiry_meta.currency}</p>
              </div>
            )}

            {/* Messages */}
            <div className="min-h-0 flex-1 space-y-2 overflow-y-auto overscroll-contain bg-muted/20 px-3 py-4 sm:px-4" data-testid="inbox-messages">
              {messages.length === 0 ? (
                <p className="text-center text-sm text-muted-foreground">Nema poruka</p>
              ) : messages.map((m, i) => {
                const prev = messages[i - 1];
                const showDay = !prev || (prev.inserted_at?.slice(0, 10) !== m.inserted_at?.slice(0, 10));
                return (
                  <div key={m.id}>
                    {showDay && (
                      <div className="my-3 text-center">
                        <span className="rounded-full bg-card px-3 py-1 text-[11px] font-medium text-muted-foreground">
                          {formatDayHeader(m.inserted_at)}
                        </span>
                      </div>
                    )}
                    <MessageBubble
                      msg={m}
                      threadId={selected.id}
                      onActionDone={async () => {
                        // Refresh messages (shows new outbound msg + hides interactive buttons)
                        const { data } = await api.get(`/inbox/threads/${selected.id}`);
                        setMessages(data.messages || []);
                        setSelected(data.thread);
                        setTimeout(() => scrollMsgsToEnd(), 50);
                        await load();
                      }}
                    />
                  </div>
                );
              })}
              <div ref={msgEndRef} />
            </div>

            {/* Composer */}
            {!selected.is_closed && (
              <OutgoingComposer key={selected.id} selected={selected} reservation={reservation} messages={messages} composer={composer} setComposer={setComposer} sending={sending} onSend={onSend}
                onAttachmentSent={async () => { const { data } = await api.get(`/inbox/threads/${selectedId}`); setMessages(data.messages || []); setSelected(data.thread); setTimeout(() => scrollMsgsToEnd(), 50); load(); }} />
            )}
            {actionMsg && (
              <div className="border-t border-border bg-amber-50 px-4 py-2 text-xs text-amber-900" data-testid="inbox-action-msg">
                {actionMsg}
                <button onClick={() => setActionMsg("")} className="ml-2 text-amber-700 hover:underline">×</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Subcomponents
// =============================================================================
function FilterChip({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`rounded-full px-2.5 py-1 text-xs font-medium transition ${
        active ? "bg-primary text-primary-foreground" : "bg-background text-foreground border border-border hover:bg-muted"
      }`}
    >
      {children}
    </button>
  );
}

function SectionHeader({ icon: Icon, title, tone = "slate" }) {
  const cls = tone === "amber"
    ? "bg-amber-50 text-amber-800 border-amber-200"
    : "bg-muted/60 text-muted-foreground border-border";
  return (
    <div className={`flex items-center gap-2 border-y px-4 py-1.5 text-[11px] font-bold uppercase tracking-wider ${cls}`}>
      <Icon size={12} weight="bold" />
      {title}
    </div>
  );
}

function ThreadRow({ thread, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`inbox-thread-${thread.id}`}
      className={`flex w-full items-start gap-3 border-b border-border px-4 py-3 text-left transition hover:bg-muted/50 ${
        selected ? "bg-primary/5" : ""
      }`}
    >
      <ProviderBadge provider={thread.provider} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <p className={`truncate text-sm ${thread.unread ? "font-bold text-foreground" : "font-medium text-foreground"}`}>
            {thread.title || "(Bez imena)"}
          </p>
          <span className="shrink-0 text-[10px] text-muted-foreground">{relTime(thread.last_message_received_at)}</span>
        </div>
        <div className="mt-0.5 flex items-center gap-1.5">
          {thread.last_message_sender === "guest" && thread.unread && (
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-rose-500" />
          )}
          <p className={`truncate text-xs ${thread.unread ? "text-foreground" : "text-muted-foreground"}`}>
            {thread.last_message_sender === "property" && "Vi: "}
            {thread.last_message_text || "(prazno)"}
          </p>
        </div>
        {thread.stay && (
          <p className="mt-0.5 truncate text-[11px] text-muted-foreground" data-testid={`inbox-thread-stay-${thread.id}`}>
            {fmtStay(thread.stay.check_in, thread.stay.check_out)}{thread.stay.unit_code ? ` · ${thread.stay.unit_code}` : ""}
          </p>
        )}
        {thread.is_closed && (
          <p className="mt-1 inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-slate-600">
            <Lock size={9} /> Zatvoreno
          </p>
        )}
      </div>
    </button>
  );
}

function MessageBubble({ msg, threadId, onActionDone }) {
  const interactive = detectInteractive(msg);
  const isProperty = msg.sender === "property";
  const isSystem = msg.sender === "system";
  const quickActionTag = msg?.meta?.quick_action;  // ovo je NAŠ outbound reply iz quick-action-a
  // Guest/property message translation state. Target lang = "hr" by default
  // (user's lang). Cache is server-side on `db.messages.translations[hr]`
  // so repeated clicks on the same bubble never re-hit Gemini.
  const [translation, setTranslation] = React.useState(msg?.translations?.hr || null);
  const [showTranslation, setShowTranslation] = React.useState(false);
  const [translating, setTranslating] = React.useState(false);
  const translate = async () => {
    if (translation) { setShowTranslation((v) => !v); return; }
    setTranslating(true);
    try {
      const { data } = await api.post("/inbox/translate", { text: msg.text || "", target_language: "hr", message_id: msg.id });
      setTranslation(data);
      setShowTranslation(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Prijevod trenutno nije dostupan.");
    } finally {
      setTranslating(false);
    }
  };
  if (isSystem) {
    return (
      <div className="my-2 text-center">
        <div className="inline-block max-w-[80%] rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-[11px] text-amber-900">
          <span className="font-bold">SISTEM:</span> {msg.text || "(sistemska poruka)"}
        </div>
      </div>
    );
  }
  // Interactive card for Booking.com system messages (early check-in, late
  // check-out, special requests, CC details, cancellation). Renders ABOVE the
  // bubble so admin sees context + inline action buttons without jumping to
  // another UI.
  if (interactive && interactive.type !== "already_acted") {
    return (
      <div className="space-y-2">
        <InteractiveActionCard
          type={interactive.type}
          detail={interactive}
          messageId={msg.id}
          threadId={threadId}
          onDone={onActionDone}
        />
        <div className="flex justify-start">
          <div className="max-w-[80%] rounded-lg bg-card px-3 py-2 text-foreground shadow-sm">
            <p className="whitespace-pre-wrap break-words text-sm">{msg.text || (msg.attachments?.length ? "" : "(prazno)")}</p>
            <MessageAttachments attachments={msg.attachments} />
            <div className="mt-1 flex items-center justify-between gap-2 text-[10px] text-muted-foreground">
              <span>{msg.inserted_at ? fmtTime(msg.inserted_at) : ""}</span>
              {msg.text && (
                <button
                  onClick={translate}
                  disabled={translating}
                  className="rounded px-1 py-0.5 opacity-80 transition hover:bg-black/5 hover:opacity-100 disabled:opacity-40"
                  data-testid={`inbox-translate-${msg.id}`}
                >
                  {translating ? "..." : translation ? (showTranslation ? "🌐 Sakrij" : "🌐 Prikaži") : "🌐 Prevedi"}
                </button>
              )}
            </div>
            {showTranslation && translation && (
              <div className="mt-2 rounded border-l-2 border-primary/40 bg-primary/5 pl-2">
                <p className="text-[10px] font-medium opacity-70">🌐 Prijevod {translation.cached ? "· (iz cache-a)" : ""}</p>
                <p className="mt-0.5 whitespace-pre-wrap break-words text-sm italic">{translation.translation}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className={`flex ${isProperty ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[80%] rounded-lg px-3 py-2 ${
        isProperty ? "bg-primary text-primary-foreground" : "bg-card text-foreground shadow-sm"
      }`}>
        {quickActionTag && (
          <p className="mb-1 inline-flex items-center gap-1 rounded-full bg-primary-foreground/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider">
            <Check size={9} weight="bold" /> Quick action: {quickActionTag}
          </p>
        )}
        {interactive && interactive.type === "already_acted" && (
          <p className="mb-1 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-700">
            <Check size={9} weight="bold" /> {interactive.action.replace(/_/g, " ")} · {interactive.by}
          </p>
        )}
        <p className="whitespace-pre-wrap break-words text-sm">{msg.text || (msg.attachments?.length ? "" : "(prazno)")}</p>
        {showTranslation && translation && (
          <div className={`mt-2 rounded border-l-2 pl-2 ${isProperty ? "border-primary-foreground/40 bg-primary-foreground/10" : "border-primary/40 bg-primary/5"}`}>
            <p className="text-[10px] font-medium opacity-70">
              🌐 Prijevod s {translation.source_language || "izvornog jezika"} → hrvatski{translation.cached ? " · (iz cache-a)" : ""}
            </p>
            <p className="mt-0.5 whitespace-pre-wrap break-words text-sm italic">{translation.translation}</p>
          </div>
        )}
        <MessageAttachments attachments={msg.attachments} light={isProperty} />
        <div className={`mt-1 flex items-center justify-between gap-2 text-[10px] ${isProperty ? "text-primary-foreground/60" : "text-muted-foreground"}`}>
          <span>{msg.inserted_at ? fmtTime(msg.inserted_at) : ""}</span>
          {msg.text && (
            <button
              onClick={translate}
              disabled={translating}
              className="rounded px-1 py-0.5 opacity-80 transition hover:bg-black/5 hover:opacity-100 disabled:opacity-40"
              title={translation ? (showTranslation ? "Sakrij prijevod" : "Prikaži prijevod") : "Prevedi na hrvatski"}
              data-testid={`inbox-translate-${msg.id}`}
            >
              {translating ? "..." : translation ? (showTranslation ? "🌐 Sakrij" : "🌐 Prikaži") : "🌐 Prevedi"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Interactive action card — renders above the message bubble with 1-click
// response buttons for Booking.com system messages. Each click posts to
// `/inbox/threads/{id}/quick-action` with a predefined EN/HR/DE template.
// =============================================================================
function InteractiveActionCard({ type, detail, messageId, threadId, onDone }) {
  const spec = INTERACTIVE_META[type];
  const [lang, setLang] = React.useState("en");
  const [busy, setBusy] = React.useState(null);  // action key currently sending
  const [showCustom, setShowCustom] = React.useState(false);
  const [customText, setCustomText] = React.useState("");
  if (!spec) return null;
  const colors = COLOR_CLASSES[spec.color] || COLOR_CLASSES.amber;
  const Icon = spec.icon;

  const send = async (actionKey, custom) => {
    setBusy(actionKey || "custom");
    try {
      const { data } = await api.post(`/inbox/threads/${threadId}/quick-action`, {
        action: actionKey || "custom",
        lang,
        custom_message: custom || null,
        message_id: messageId,
      });
      toast.success(`Odgovor poslan (${data.lang.toUpperCase()})`, { description: data.sent_text?.slice(0, 100) });
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Slanje nije uspjelo.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className={`rounded-lg border-2 ${colors.border} ${colors.bg} p-3 shadow-sm`} data-testid={`inbox-interactive-${type}`}>
      <div className="flex items-start gap-3">
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${colors.iconBg}`}>
          <Icon size={18} weight="duotone" className={colors.iconFg} />
        </div>
        <div className="min-w-0 flex-1">
          <p className={`text-sm font-bold ${colors.fg}`}>{spec.title}</p>
          <p className={`mt-0.5 text-xs ${colors.fg} opacity-80`}>{spec.desc(detail)}</p>
          {detail.heuristic && (
            <p className="mt-1 text-[10px] italic text-muted-foreground">
              (automatska detekcija iz teksta — provjerite originalnu poruku ispod)
            </p>
          )}
        </div>
        {spec.actions.length > 0 && (
          <select
            value={lang}
            onChange={(e) => setLang(e.target.value)}
            className={`shrink-0 rounded border ${colors.border} bg-white px-1.5 py-0.5 text-[11px] font-medium focus:outline-none`}
            data-testid={`inbox-interactive-lang-${type}`}
          >
            <option value="en">EN</option>
            <option value="hr">HR</option>
            <option value="de">DE</option>
          </select>
        )}
      </div>
      {spec.actions.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {spec.actions.map((a) => {
            const AIcon = a.icon;
            return (
              <button
                key={a.key}
                onClick={() => send(a.key)}
                disabled={!!busy}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition disabled:opacity-50 ${TONE_CLASSES[a.tone]}`}
                data-testid={`inbox-interactive-action-${a.key}`}
              >
                {busy === a.key ? <CircleNotch size={13} className="animate-spin" /> : <AIcon size={13} weight="bold" />}
                {a.label}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => setShowCustom((v) => !v)}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
            data-testid={`inbox-interactive-custom-toggle-${type}`}
          >
            {showCustom ? "Odustani" : "Odgovori drukčije"}
          </button>
        </div>
      )}
      {showCustom && (
        <div className="mt-2 space-y-2">
          <textarea
            rows={2}
            value={customText}
            onChange={(e) => setCustomText(e.target.value)}
            placeholder="Upiši vlastiti odgovor gostu..."
            className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm focus:border-primary focus:outline-none"
            data-testid={`inbox-interactive-custom-text-${type}`}
          />
          <button
            onClick={() => send(null, customText)}
            disabled={!customText.trim() || !!busy}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
            data-testid={`inbox-interactive-custom-send-${type}`}
          >
            {busy === "custom" ? <CircleNotch size={13} className="animate-spin" /> : <PaperPlaneTilt size={13} weight="fill" />}
            Pošalji moj odgovor
          </button>
        </div>
      )}
    </div>
  );
}

const LIVE_FEED_META = {
  inquiry: { icon: Question, color: "amber", title: "Upit za rezervaciju (Airbnb)" },
  reservation_request: { icon: Hourglass, color: "sky", title: "Zahtjev za rezervaciju (Airbnb)" },
  alteration_request: { icon: CalendarCheck, color: "indigo", title: "Zahtjev za izmjenu rezervacije (Airbnb)" },
};

function LiveFeedActionCard({ thread, onDone }) {
  const spec = LIVE_FEED_META[thread.live_feed_type];
  const d = thread.live_feed_details || {};
  const [busy, setBusy] = React.useState(false);
  const [mode, setMode] = React.useState(null); // null | "special_offer" | "decline"
  const [price, setPrice] = React.useState(d.payout_amount || "");
  const [reason, setReason] = React.useState("dates_not_available");
  if (!spec) return null;
  const colors = COLOR_CLASSES[spec.color] || COLOR_CLASSES.amber;
  const Icon = spec.icon;

  const resolve = async (action, extra) => {
    setBusy(true);
    try {
      await api.post(`/inbox/threads/${thread.id}/live-feed/resolve`, { action, ...extra });
      toast.success("Spremljeno — odgovor je poslan na Airbnb.");
      setMode(null);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Akcija nije uspjela.");
    } finally {
      setBusy(false);
    }
  };

  const confirmAndResolve = (action, label, extra) => {
    if (!window.confirm(`${label} — ova odluka je trajna i šalje se izravno na Airbnb. Nastaviti?`)) return;
    resolve(action, extra);
  };

  const guestsText = [d.number_of_adults, d.number_of_children].filter(Boolean).join("+");

  return (
    <div className={`rounded-lg border-2 ${colors.border} ${colors.bg} p-3 shadow-sm`} data-testid={`inbox-livefeed-${thread.live_feed_type}`}>
      <div className="flex items-start gap-3">
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${colors.iconBg}`}>
          <Icon size={18} weight="duotone" className={colors.iconFg} />
        </div>
        <div className="min-w-0 flex-1">
          <p className={`text-sm font-bold ${colors.fg}`}>{spec.title}</p>
          {d.listing_name && (
            <p className={`mt-0.5 text-xs font-semibold ${colors.fg}`} data-testid="inbox-livefeed-listing-name">{d.listing_name}</p>
          )}
          <p className={`mt-0.5 text-xs ${colors.fg} opacity-80`} data-testid="inbox-livefeed-details">
            {d.checkin_date} → {d.checkout_date}
            {guestsText && <> · {guestsText} {Number(d.number_of_adults) + Number(d.number_of_children || 0) === 1 ? "gost" : "gostiju"}</>}
            {d.payout_amount != null && <> · {d.payout_amount} {d.currency || "EUR"}</>}
          </p>
        </div>
        {thread.live_feed_resolved && (
          <span className="shrink-0 rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-semibold text-slate-700">RIJEŠENO</span>
        )}
      </div>

      {!thread.live_feed_resolved && mode === null && (
        <div className="mt-3 flex flex-wrap gap-2">
          {thread.live_feed_type === "inquiry" && (
            <>
              <button onClick={() => confirmAndResolve("preapprove", "Predodobravanje")} disabled={busy}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold disabled:opacity-50 ${TONE_CLASSES.success}`}
                data-testid="inbox-livefeed-preapprove">
                {busy ? <CircleNotch size={13} className="animate-spin" /> : <ThumbsUp size={13} weight="bold" />} Predodobri
              </button>
              <button onClick={() => setMode("special_offer")} disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                data-testid="inbox-livefeed-special-offer-toggle">
                <Coins size={13} weight="bold" /> Pošalji posebnu ponudu
              </button>
            </>
          )}
          {thread.live_feed_type === "reservation_request" && (
            <>
              <button onClick={() => confirmAndResolve("accept", "Prihvaćanje zahtjeva")} disabled={busy}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold disabled:opacity-50 ${TONE_CLASSES.success}`}
                data-testid="inbox-livefeed-accept">
                {busy ? <CircleNotch size={13} className="animate-spin" /> : <ThumbsUp size={13} weight="bold" />} Prihvati
              </button>
              <button onClick={() => setMode("decline")} disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                data-testid="inbox-livefeed-decline-toggle">
                <ThumbsDown size={13} weight="bold" /> Odbij
              </button>
            </>
          )}
          {thread.live_feed_type === "alteration_request" && (
            <>
              <button onClick={() => confirmAndResolve("accept", "Prihvaćanje izmjene")} disabled={busy}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold disabled:opacity-50 ${TONE_CLASSES.success}`}
                data-testid="inbox-livefeed-accept">
                {busy ? <CircleNotch size={13} className="animate-spin" /> : <ThumbsUp size={13} weight="bold" />} Prihvati izmjenu
              </button>
              <button onClick={() => confirmAndResolve("decline", "Odbijanje izmjene")} disabled={busy}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold disabled:opacity-50 ${TONE_CLASSES.danger}`}
                data-testid="inbox-livefeed-decline">
                <ThumbsDown size={13} weight="bold" /> Odbij
              </button>
              <button onClick={() => confirmAndResolve("cancel", "Otkazivanje zahtjeva")} disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                data-testid="inbox-livefeed-cancel">
                <Prohibit size={13} weight="bold" /> Otkaži zahtjev
              </button>
            </>
          )}
        </div>
      )}

      {mode === "special_offer" && (
        <div className="mt-3 space-y-2">
          <label className="flex items-center gap-2 text-xs font-medium text-slate-700">
            <Tag size={13} /> Nova ukupna cijena ({d.currency || "EUR"})
            <input type="number" min="0" value={price} onChange={(e) => setPrice(e.target.value)}
              className="w-28 rounded border border-slate-300 px-2 py-1 text-sm" data-testid="inbox-livefeed-price-input" />
          </label>
          <div className="flex gap-2">
            <button onClick={() => confirmAndResolve("special_offer", "Slanje posebne ponude", { total_price: Number(price) })}
              disabled={busy || !price} className={`rounded-md px-3 py-1.5 text-xs font-semibold disabled:opacity-50 ${TONE_CLASSES.success}`}
              data-testid="inbox-livefeed-special-offer-send">
              {busy ? <CircleNotch size={13} className="animate-spin" /> : "Pošalji ponudu"}
            </button>
            <button onClick={() => setMode(null)} className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium hover:bg-slate-50" data-testid="inbox-livefeed-special-offer-cancel">Odustani</button>
          </div>
        </div>
      )}

      {mode === "decline" && (
        <div className="mt-3 space-y-2">
          <select value={reason} onChange={(e) => setReason(e.target.value)}
            className="w-full rounded border border-slate-300 px-2 py-1.5 text-xs" data-testid="inbox-livefeed-decline-reason">
            <option value="dates_not_available">Datumi nisu dostupni</option>
            <option value="not_a_good_fit">Ne odgovara mi gost/boravak</option>
            <option value="waiting_for_better_reservation">Čekam bolju rezervaciju</option>
            <option value="not_comfortable">Ostalo</option>
          </select>
          <div className="flex gap-2">
            <button onClick={() => confirmAndResolve("decline", "Odbijanje zahtjeva", { reason })}
              disabled={busy} className={`rounded-md px-3 py-1.5 text-xs font-semibold disabled:opacity-50 ${TONE_CLASSES.danger}`}
              data-testid="inbox-livefeed-decline-send">
              {busy ? <CircleNotch size={13} className="animate-spin" /> : "Potvrdi odbijanje"}
            </button>
            <button onClick={() => setMode(null)} className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium hover:bg-slate-50" data-testid="inbox-livefeed-decline-cancel">Odustani</button>
          </div>
        </div>

      )}
    </div>
  );
}

const LANG_NAMES = {
  en: "Engleski", de: "Njemački", it: "Talijanski", fr: "Francuski", es: "Španjolski", nl: "Nizozemski", pl: "Poljski",
  cs: "Češki", sk: "Slovački", sl: "Slovenski", hu: "Mađarski", sv: "Švedski", da: "Danski", no: "Norveški", fi: "Finski",
  pt: "Portugalski", ru: "Ruski", uk: "Ukrajinski", ja: "Japanski", zh: "Kineski", ko: "Korejski", tr: "Turski", hr: "Hrvatski",
};
const langName = (c) => LANG_NAMES[c] || (c ? c.toUpperCase() : "");

function useGuestLanguage(reservation, messages) {
  const [lang, setLang] = React.useState(null);
  const lastGuest = React.useMemo(() => [...(messages || [])].reverse().find((m) => m.sender === "guest" && (m.text || "").trim().length > 3), [messages]);
  React.useEffect(() => {
    const fromRes = (reservation?.guest_language || "").slice(0, 2).toLowerCase();
    if (fromRes) { setLang(fromRes); return; }
    if (!lastGuest) return;
    const cached = Object.values(lastGuest.translations || {})[0]?.source_language;
    if (cached) { setLang(cached.slice(0, 2)); return; }
    api.post("/inbox/translate", { text: lastGuest.text, target_language: "hr", message_id: lastGuest.id })
      .then(({ data }) => data.source_language && setLang(data.source_language.slice(0, 2))).catch(() => {});
  }, [reservation?.guest_language, lastGuest?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  return lang;
}

function OutgoingComposer({ selected, reservation, messages, composer, setComposer, sending, onSend, onAttachmentSent }) {
  const composerRef = React.useRef(null);
  const guestLang = useGuestLanguage(reservation, messages);
  const [targetLang, setTargetLang] = React.useState("");
  const [preview, setPreview] = React.useState(null); // {source, translation, edited}
  const [translating, setTranslating] = React.useState(false);
  const langs = React.useMemo(() => {
    const codes = Object.keys(LANG_NAMES);
    if (targetLang && !codes.includes(targetLang)) codes.unshift(targetLang);
    return codes;
  }, [targetLang]);

  const translate = async () => {
    const text = composer.trim();
    if (!text || !targetLang) return;
    setTranslating(true);
    try {
      const { data } = await api.post("/inbox/translate", { text, target_language: targetLang });
      if (data.source_language === targetLang) { onSend(text); return; }
      setPreview({ source: text, translation: data.translation, from: data.source_language });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Prijevod trenutno nije dostupan.");
    } finally {
      setTranslating(false);
    }
  };
  const primary = () => {
    if (!composer.trim() || sending || translating) return;
    if (preview && preview.source === composer.trim()) { send(preview.translation); return; }
    if (targetLang) translate(); else onSend();
  };
  const send = async (text) => { if (await onSend(text)) setPreview(null); };
  const primaryLabel = preview ? `Pošalji prijevod (${targetLang.toUpperCase()})` : targetLang ? `Prevedi na ${targetLang.toUpperCase()}` : "Pošalji";

  return (
    <div className="border-t border-border bg-card p-3">
      <div className="mb-2 flex flex-wrap items-center gap-2 text-[11px]" data-testid="inbox-translate-bar">
        <span className="inline-flex items-center gap-1 text-muted-foreground"><Translate size={13} /> Šalji na:</span>
        <select
          value={targetLang}
          onChange={(e) => { setTargetLang(e.target.value); setPreview(null); }}
          className="rounded-md border border-foreground/20 bg-card px-2 py-1 text-[11px] font-medium focus:border-primary focus:outline-none"
          data-testid="inbox-translate-lang"
        >
          <option value="">Bez prijevoda (original)</option>
          {langs.map((c) => <option key={c} value={c}>{langName(c)}{c === guestLang ? " · jezik gosta" : ""}</option>)}
        </select>
        {guestLang && guestLang !== "hr" && (
          <span className="rounded-full bg-primary/10 px-2 py-0.5 font-semibold text-primary" data-testid="inbox-guest-lang">Gost piše: {langName(guestLang)}</span>
        )}
        <span className="ml-auto hidden text-[10px] text-muted-foreground sm:inline">Ctrl + Enter</span>
      </div>
      {preview && (
        <div className="mb-2 rounded-md border border-primary/30 bg-primary/5 p-2.5" data-testid="inbox-translate-preview">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-primary">Prijevod na {langName(targetLang)} — gost će primiti ovo</p>
          <textarea
            rows={3}
            value={preview.translation}
            onChange={(e) => setPreview((p) => ({ ...p, translation: e.target.value }))}
            className="mt-1 w-full resize-none rounded-md border border-foreground/15 bg-card px-2.5 py-1.5 text-sm focus:border-primary focus:outline-none"
            data-testid="inbox-translate-preview-text"
          />
          <div className="mt-2 flex flex-wrap gap-2">
            <button onClick={() => send(preview.translation)} disabled={sending || !preview.translation.trim()} className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-[11px] font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50" data-testid="inbox-send-translated">
              <PaperPlaneTilt size={12} weight="fill" /> Pošalji prijevod
            </button>
            <button onClick={() => send(preview.source)} disabled={sending} className="rounded-md border border-border bg-card px-3 py-1.5 text-[11px] font-medium hover:bg-muted" data-testid="inbox-send-original">Pošalji original</button>
            <button onClick={() => setPreview(null)} className="rounded-md px-2 py-1.5 text-[11px] text-muted-foreground hover:bg-muted" data-testid="inbox-translate-cancel">Uredi poruku</button>
          </div>
        </div>
      )}
      <div className="flex items-end gap-2">
        <textarea
          rows={2}
          placeholder={targetLang ? `Piši na hrvatskom — prevest ćemo na ${langName(targetLang).toLowerCase()}...` : "Odgovori gostu..."}
          value={composer}
          onChange={(e) => { setComposer(e.target.value); setPreview(null); }}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); primary(); } }}
          className="flex-1 resize-none rounded-md border border-foreground/20 bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
          data-testid="inbox-composer"
          ref={composerRef}
        />
        <AttachmentButton threadId={selected.id} disabled={selected.is_closed} onSent={onAttachmentSent} />
        <TemplatePicker reservationId={reservation?.id || selected?.reservation_id} threadId={selected?.id} onPick={(text) => { setComposer((c) => (c.trim() ? `${c.trimEnd()}\n\n${text}` : text)); setPreview(null); requestAnimationFrame(() => composerRef.current?.focus()); }} />
        <EmojiPicker onPick={(em) => {
          const el = composerRef.current;
          const pos = el ? el.selectionStart ?? composer.length : composer.length;
          const next = composer.slice(0, pos) + em + composer.slice(el ? el.selectionEnd ?? pos : pos);
          setComposer(next); setPreview(null);
          requestAnimationFrame(() => { if (el) { el.focus(); el.selectionStart = el.selectionEnd = pos + em.length; } });
        }} />
        <button
          onClick={primary}
          disabled={sending || translating || !composer.trim()}
          className="flex items-center gap-1.5 self-stretch rounded-md bg-primary px-3 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-40"
          title={primaryLabel}
          data-testid="inbox-send-btn"
        >
          {sending || translating ? <CircleNotch size={16} className="animate-spin" /> : targetLang && !preview ? <Translate size={16} weight="bold" /> : <PaperPlaneTilt size={16} weight="fill" />}
          <span className="hidden sm:inline">{primaryLabel}</span>
        </button>
      </div>
    </div>
  );
}

function EmptyState({ onSync, syncing }) {
  return (
    <div className="flex h-full flex-col items-center justify-center px-8 py-12 text-center text-muted-foreground">
      <ChatCircleDots size={48} weight="duotone" className="mb-3 opacity-40" />
      <p className="text-sm font-medium">Nema razgovora</p>
      <p className="mt-1 text-xs">Kliknite &quot;Osvježi&quot; da povučete poruke iz OTA, ili pričekajte da gosti pošalju prvu poruku.</p>
      <button onClick={onSync} disabled={syncing} className="mt-4 flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50">
        {syncing ? <CircleNotch size={14} className="animate-spin" /> : <ArrowsClockwise size={14} />} Sinkroniziraj sada
      </button>
    </div>
  );
}
